import { memo, useMemo } from 'react';
import type { CSSProperties, JSX } from 'react';
import { Table, TypographyStylesProvider } from '@mantine/core';
import ReactMarkdown, { type Components, type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { HeadingInfo } from './sections';
import { resolveHelpImageSrc } from './helpImages';

interface MarkdownProps {
  /** Raw markdown to render. */
  content: string;
  /** Precomputed headings for exactly this `content` string (from
   * sections.ts's parseDoc / extractParsedSection, ultimately content.ts's
   * module-scope PARSED_DOCS). `line` must be the 0-based line of each
   * heading within `content`; `slug` is the id stamped onto the rendered
   * tag. Passing precomputed ids — rather than slugging during render — is
   * what keeps this component pure (no mutable slugger, safe under
   * StrictMode/concurrent rendering) and guarantees a SINGLE slug path:
   * the rendered id is by construction the same value helpMap.test.ts
   * verified against the doc, even for headings containing inline markdown
   * (bold/code/links), where re-slugging rendered text would diverge from
   * slugging the raw source line. */
  headings: HeadingInfo[];
}

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type HeadingProps = JSX.IntrinsicElements['h1'] & ExtraProps;
type ImageProps = JSX.IntrinsicElements['img'] & ExtraProps;
type BlockquoteProps = JSX.IntrinsicElements['blockquote'] & ExtraProps;
type PreProps = JSX.IntrinsicElements['pre'] & ExtraProps;
type CodeProps = JSX.IntrinsicElements['code'] & ExtraProps;
type TableProps = JSX.IntrinsicElements['table'] & ExtraProps;
type TableSectionProps = JSX.IntrinsicElements['thead'] & ExtraProps;
type TableRowProps = JSX.IntrinsicElements['tr'] & ExtraProps;
type TableHeadCellProps = JSX.IntrinsicElements['th'] & ExtraProps;
type TableDataCellProps = JSX.IntrinsicElements['td'] & ExtraProps;
type UnorderedListProps = JSX.IntrinsicElements['ul'] & ExtraProps;
type OrderedListProps = JSX.IntrinsicElements['ol'] & ExtraProps;
type ListItemProps = JSX.IntrinsicElements['li'] & ExtraProps;

/** Renders an in-guide screenshot. Module-scope (a stable component identity,
 * so a re-render never remounts the images) and independent of the per-doc
 * heading map. Its ONE job beyond a plain `<img>` is rewriting the guide's
 * relative `images/…` src to the served `/help-images/…` path (helpImages.ts).
 * `node` is dropped rather than spread onto the DOM element; `alt` always
 * renders (the docs carry descriptive alt text — helpImages.test.ts enforces
 * it — which is also the graceful fallback if a copy step ever went missing). */
function GuideImage({ src, alt, node: _node, ...rest }: ImageProps) {
  return (
    <img
      {...rest}
      src={resolveHelpImageSrc(typeof src === 'string' ? src : undefined)}
      alt={alt ?? ''}
      loading="lazy"
      style={{
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 8,
        border: '1px solid var(--mantine-color-gray-3)',
      }}
    />
  );
}

// Per-tag spacing/hierarchy on top of TypographyStylesProvider's own
// defaults: a visible size/weight step between chapters (h2 — a bottom rule
// marks it as a section break) vs sub-headings (h3+, spacing only), and a
// zeroed top margin on h1 because HelpCentre.tsx already separates guides
// with a `Stack gap="xl"` — a further h1 margin would double that gap.
const HEADING_SPACING: Record<HeadingTag, CSSProperties> = {
  h1: { marginTop: 0, marginBottom: 'var(--mantine-spacing-md)' },
  h2: {
    marginTop: 'var(--mantine-spacing-xl)',
    paddingBottom: 'var(--mantine-spacing-xs)',
    borderBottom: '1px solid var(--mantine-color-gray-3)',
  },
  h3: { marginTop: 'var(--mantine-spacing-lg)' },
  h4: { marginTop: 'var(--mantine-spacing-md)' },
  h5: { marginTop: 'var(--mantine-spacing-md)' },
  h6: { marginTop: 'var(--mantine-spacing-md)' },
};

/** Builds the six heading renderers, each stamping the precomputed id for
 * its source line. react-markdown passes the hast `node` (with remark's
 * 1-based source position, relative to the `content` string it parsed) to
 * custom components, so the lookup is a pure Map hit — no counters, no
 * occurrence tracking, no render-order dependence. A heading with no map
 * entry (e.g. a setext heading, which parseDoc deliberately doesn't parse)
 * renders without an id rather than with a wrong one. */
function buildHeadingComponents(idByLine: ReadonlyMap<number, string>): Pick<Components, HeadingTag> {
  function makeHeading(Tag: HeadingTag) {
    return function HeadingRenderer({ children, node, style, ...rest }: HeadingProps) {
      const line = node?.position?.start.line;
      const id = line === undefined ? undefined : idByLine.get(line);
      return (
        <Tag {...rest} id={id} style={{ ...HEADING_SPACING[Tag], ...style }}>
          {children}
        </Tag>
      );
    };
  }
  return {
    h1: makeHeading('h1'),
    h2: makeHeading('h2'),
    h3: makeHeading('h3'),
    h4: makeHeading('h4'),
    h5: makeHeading('h5'),
    h6: makeHeading('h6'),
  };
}

/** Callout styling for guide asides — a graphite left border plus a neutral
 * tint (branded-neutral ruling, 2026-08-24: accents are functional, never
 * decorative, and never the browser/Typography default blockquote blue).
 * None of the three guides use `>` blockquotes today, but the markdown
 * source can gain one at any time and this is the only place it would be
 * styled — cheaper to have it ready than to notice it's plain later. */
function Blockquote({ children, node: _node, style, ...rest }: BlockquoteProps) {
  return (
    <blockquote
      {...rest}
      style={{
        margin: 0,
        padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-md)',
        borderLeft: '4px solid var(--mantine-color-graphite-4)',
        borderRadius: 'var(--mantine-radius-sm)',
        background: 'var(--mantine-color-gray-0)',
        color: 'var(--mantine-color-dimmed)',
        ...style,
      }}
    >
      {children}
    </blockquote>
  );
}

/** Fenced code blocks (`pre` wrapping `code`) get the bordered block
 * treatment; a bare code span (no wrapping `pre`) gets a compact inline pill
 * instead. react-markdown dropped the `inline` prop on `code` in v6 (the
 * CommonMark AST doesn't distinguish them cleanly), so this uses the same
 * two signals their own examples do: a `language-xxx` className (present
 * whenever a fence names a language) or a literal newline in the content
 * (present for any multi-line fence, named or not). The one guide fence with
 * neither — USER_GUIDE.md's single-line, unnamed `dry mass = …` block —
 * renders as a pill rather than a full bordered box; still legible, and not
 * worth a fussier heuristic for one line. */
function Code({ className, children, node: _node, style, ...rest }: CodeProps) {
  const isBlock = /language-[\w-]+/.test(className ?? '') || String(children).includes('\n');

  if (isBlock) {
    return (
      <code
        className={className}
        {...rest}
        style={{ fontFamily: 'var(--mantine-font-family-monospace)', fontSize: '0.85em', ...style }}
      >
        {children}
      </code>
    );
  }

  return (
    <code
      {...rest}
      style={{
        fontFamily: 'var(--mantine-font-family-monospace)',
        fontSize: '0.85em',
        background: 'var(--mantine-color-gray-1)',
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-sm)',
        padding: '0.1em 0.4em',
        ...style,
      }}
    >
      {children}
    </code>
  );
}

function Pre({ children, node: _node, style, ...rest }: PreProps) {
  return (
    <pre
      {...rest}
      style={{
        background: 'var(--mantine-color-gray-0)',
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-md)',
        padding: 'var(--mantine-spacing-md)',
        overflowX: 'auto',
        ...style,
      }}
    >
      {children}
    </pre>
  );
}

// GFM tables (remark-gfm) rendered through Mantine's actual `Table` parts
// rather than styled `<table>`/`<td>` tags — `striped`/`highlightOnHover`
// only apply via Table's own React context (see Table.context.mjs), which
// fires for `Table.Tr`/`Table.Td`/etc, never for plain HTML table tags. So
// every row/cell/section needs its own override, not just the outer table.
// `Table.ScrollContainer` keeps a wide table (the docs have several) from
// forcing the whole guide to scroll horizontally on a phone.
function MdTable({ children, node: _node }: TableProps) {
  return (
    <Table.ScrollContainer minWidth={420}>
      <Table striped highlightOnHover withTableBorder verticalSpacing="xs">
        {children}
      </Table>
    </Table.ScrollContainer>
  );
}
function MdThead({ children, node: _node }: TableSectionProps) {
  return <Table.Thead>{children}</Table.Thead>;
}
function MdTbody({ children, node: _node }: TableSectionProps) {
  return <Table.Tbody>{children}</Table.Tbody>;
}
function MdTr({ children, node: _node }: TableRowProps) {
  return <Table.Tr>{children}</Table.Tr>;
}
// `...rest` carries remark-gfm's column-alignment `style` (`text-align`) —
// forwarded, not replaced.
function MdTh({ children, node: _node, ...rest }: TableHeadCellProps) {
  return <Table.Th {...rest}>{children}</Table.Th>;
}
function MdTd({ children, node: _node, ...rest }: TableDataCellProps) {
  return <Table.Td {...rest}>{children}</Table.Td>;
}

// Plain block styling only (no `display: flex` — flex/grid containers never
// generate `::marker` boxes for `list-item` children per the CSS spec, which
// would silently kill bullet/number markers on every list in the guides).
const LIST_STYLE: CSSProperties = { paddingLeft: '1.4em', lineHeight: 1.6 };

function UnorderedList({ children, node: _node, style, ...rest }: UnorderedListProps) {
  return (
    <ul {...rest} style={{ ...LIST_STYLE, ...style }}>
      {children}
    </ul>
  );
}
function OrderedList({ children, node: _node, style, ...rest }: OrderedListProps) {
  return (
    <ol {...rest} style={{ ...LIST_STYLE, ...style }}>
      {children}
    </ol>
  );
}
function ListItem({ children, node: _node, style, ...rest }: ListItemProps) {
  return (
    <li {...rest} style={{ marginBottom: 'var(--mantine-spacing-xs)', ...style }}>
      {children}
    </li>
  );
}

/** Renders help-guide markdown with GitHub-flavoured tables (remark-gfm —
 * the guides use GFM tables throughout) inside Mantine's
 * TypographyStylesProvider, so headings/lists/tables inherit the app's
 * typography instead of unstyled browser defaults. `maxWidth` caps the
 * reading measure (roughly 75 characters) so a wide guides column doesn't
 * stretch body text edge-to-edge — narrower surfaces (the HelpDrawer, the
 * quick-reference cards) are already under that width, so this only ever
 * constrains the guides tab.
 *
 * Security note (load-bearing assumption): content is trusted — the three
 * repo guides bundled at build time, never user input. Even so, raw HTML in
 * the markdown is NOT rendered: there is no rehype-raw here, so react-
 * markdown's default remark-rehype pass drops HTML nodes entirely
 * (markdown.test.tsx pins this behaviour).
 *
 * Memoised twice over: `memo` skips re-renders while props are unchanged
 * (they're module-scope constants in practice, so the remark parse runs
 * once per mount), and the `components` map is `useMemo`d so React never
 * sees new heading component types on a re-render (which would unmount and
 * remount every heading in the document). Everything below except the
 * headings is a stable, module-scope component and doesn't need to be part
 * of that memo key. */
export const Markdown = memo(function Markdown({ content, headings }: MarkdownProps) {
  const components = useMemo((): Components => {
    // remark positions are 1-based; HeadingInfo.line is 0-based.
    const idByLine = new Map(headings.map((h) => [h.line + 1, h.slug]));
    return {
      ...buildHeadingComponents(idByLine),
      img: GuideImage,
      blockquote: Blockquote,
      pre: Pre,
      code: Code,
      table: MdTable,
      thead: MdThead,
      tbody: MdTbody,
      tr: MdTr,
      th: MdTh,
      td: MdTd,
      ul: UnorderedList,
      ol: OrderedList,
      li: ListItem,
    };
  }, [headings]);

  return (
    <TypographyStylesProvider style={{ maxWidth: '75ch' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </TypographyStylesProvider>
  );
});
